import type {
  FacilityNormalized,
  ScorecardImageType,
  ScorecardTextType,
} from "EntityTypes";
import type {
  CourseListMessage,
  RoundEndedMessage,
  RoundUpdateMessage,
  RoundV2BeganMessage,
  RoundV2UpdateMessage,
  RoundV2EndedMessage,
  ScorecardMessage,
  SendRoundRangeToSimMessage,
  ServiceRequestMessage,
  SimulatorStatusMessage,
  TimerUpdateMessage,
  ClubhouseUpdateMessage,
  RoundBeganMessage,
} from "Messages";
import type {
  Course,
  SimulatorScreenShotsDto,
  ServiceRequest,
  SimulatorState,
  Clubhouse_SimulatorDevice,
} from "Models";
import { updateEntities } from "redux-query";
import {
  withCallbacks,
  signalMiddleware,
  HubConnectionBuilder,
  LogLevel,
  type SignalDispatch,
} from "updated-redux-signalr";
//import store from "store/rootReducer";
import type {
  CourseEntitiesState,
  EntitiesById,
  RootState,
  RoundsEntitiesState,
  ScreenshotsEntityType,
  ServiceRequestEntityState,
  SimulatorsEntitiesState,
} from "StoreTypes";
import { ServiceRequestType, SimStatus } from "types/enums";
import { serviceRequestEnumDisplayName } from "utils/enums/enumHelper";
import {
  GenericNotificationType,
  showCustomNotification,
} from "utils/notifications/notificationHelpers";
import { timerStringToTimerArray } from "utils/timer/timerHelpers";
import { setSelectedSimulatorReservation } from "ui-modules/simulator-reservation/simulatorReservationSlice";
import { modalAction } from "ui-modules/modals/modalSlice";
import { ModalType } from "utils/modals/modalHelpers";
import toast from "react-hot-toast";
import { DateTime } from "luxon";
import {
  getSimulator,
  getSimulators,
} from "utils/messages/clubhouseUpdateUtils";
import { getCurrentJWT } from "utils/tokens/tokenHelpers";
import { config } from "@/config/env";
import type { AnyAction } from "redux";
import { mapScreenshotDto } from "../models/modelHelpers";

const token = () => getCurrentJWT();

const notificationMp3 = "/notification.mp3";

const client = `${config.wssBaseUrl}/sockets/control`;
const notification = new Audio(notificationMp3);
const playSound = async () => {
  await notification.play();
};

type GetState = () => RootState;

// SignalDispatch<State, Action> - this extends Dispatch
type AppSignalDispatch = SignalDispatch<RootState, AnyAction>;

/* const messageSimulatorBelongToUser = (messageSimulatorId: string): boolean => {
  const userSimulatorIds: string[] = store.getState().entities?.simulators
    ? Object.keys(store.getState().entities.simulators)
    : [];

  return userSimulatorIds?.includes(messageSimulatorId);
}; */

const messageSimulatorBelongToUser = (
  getState: GetState,
  messageSimulatorId: string,
): boolean => {
  const simulators = getState().entities?.simulators;
  return simulators
    ? Object.keys(simulators).includes(messageSimulatorId)
    : false;
};

/* const getFacilityById = (facilityId: string): FacilityNormalized => {
  const selectedFacility: FacilityNormalized = store.getState().entities
    ?.facilities?.[facilityId];

  return selectedFacility;
};

function getCurrentFacilityId() {
  return store.getState().facility.currentFacilityId;
}

const getSimulatorNameById = (simulatorId: string): string => {
  const selectedSimulator: FacilityNormalized = store.getState().entities
    ?.simulators?.[simulatorId];

  return selectedSimulator.name;
}; */

const getFacilityById = (
  getState: GetState,
  facilityId: string,
): FacilityNormalized | undefined => {
  return getState().entities?.facilities?.[facilityId];
};

const getCurrentFacilityId = (getState: GetState): string => {
  return getState().facility.currentFacilityId;
};

const getSimulatorNameById = (
  getState: GetState,
  simulatorId: string,
): string => {
  return getState().entities?.simulators?.[simulatorId]?.name ?? "";
};

const isMessageObject = (value: unknown): value is object =>
  typeof value === "object" && value !== null;

function parseIfString<T>(
  value: unknown,
  guard: (v: unknown) => v is object,
): T {
  if (guard(value)) return value as T;

  if (typeof value === "string") {
    const raw = JSON.parse(value) as unknown;
    if (guard(raw)) return raw as T;
  }

  throw new Error("Input is neither T nor valid JSON string");
}

export const connection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(client, {
    accessTokenFactory: () => token(),
  })
  .withAutomaticReconnect()
  .build();

const callbacks = withCallbacks<RootState, AppSignalDispatch>()
  .add(
    "ReceiveCourseListMessage",
    (message: CourseListMessage | string) => (dispatch) => {
      const parsedMessage = parseIfString<CourseListMessage>(
        message,
        isMessageObject,
      );

      const obj: { [id: string]: Course } = {};
      parsedMessage.courses.forEach(
        (course: Course) => (obj[course.shortName] = course),
      );
      delete obj["range"];
      dispatch(
        updateEntities({
          courses: (prev: CourseEntitiesState) => ({
            ...(prev || {}),
            ...{ [parsedMessage.simulatorId]: obj },
          }),
        }),
      );
    },
  )
  .add(
    "ReceiveSendRoundRangeToSimMessage",
    (message: SendRoundRangeToSimMessage | string) => (dispatch, getState) => {
      const currentFacilityId = getCurrentFacilityId(getState); //useGetCurrentLocalFacilityId();
      console.log(
        "from ReceiveSendRoundRangeToSimMessage currentFacilityId: ",
        currentFacilityId,
      );
      const parsed = parseIfString<SendRoundRangeToSimMessage>(
        message,
        isMessageObject,
      );

      void playSound();

      if (currentFacilityId === parsed.sendRoundRangeToSimInfo?.facilityId) {
        dispatch(
          setSelectedSimulatorReservation({
            selectedSimulatorReservation: {
              ...parsed.sendRoundRangeToSimInfo,
              startTime: parsed.sendRoundRangeToSimInfo.startTime,
              endTime:
                DateTime.fromISO(
                  parsed.sendRoundRangeToSimInfo?.startTime ||
                    DateTime.now().toString(),
                )
                  .plus({
                    minutes: parsed?.sendRoundRangeToSimInfo?.reservedDuration,
                  })
                  .toString() || "",
            },
          }),
        );
        dispatch(
          modalAction({
            isOpen: true,
            modalName: ModalType.RexReservationModal,
          }),
        );

        showCustomNotification(
          `A new reservation has been made for ${parsed.sendRoundRangeToSimInfo?.numberInParty} players. Confirm reservation and send to sim to begin.`,
          GenericNotificationType.SUCCESS,
        );
      } else {
        const selectedFacility = getFacilityById(
          getState,
          parsed.sendRoundRangeToSimInfo?.facilityId,
        );
        showCustomNotification(
          `A new reservation has been made for ${selectedFacility?.name ?? "this facility"}`,
          GenericNotificationType.SUCCESS,
        );
      }
    },
  )
  .add(
    "receivesimulatorservicerequestmessage",
    (message: ServiceRequestMessage | string) => (dispatch, getState) => {
      const parsed = parseIfString<ServiceRequestMessage>(
        message,
        isMessageObject,
      );
      const request = parsed.request;

      if (messageSimulatorBelongToUser(getState, parsed.simulatorId)) {
        void playSound();

        if (request.type === ServiceRequestType.CC_REQUEST_STUCK) {
          toast.success(
            `${getSimulatorNameById(
              getState,
              parsed.simulatorId,
            )} is having an issue starting an event and needs immediate assistance.`,
            { duration: 15000, id: "facility-update-toast", icon: `‼️` },
          );
        } else {
          showCustomNotification(
            `${serviceRequestEnumDisplayName(
              ServiceRequestType[request.type],
            )} Request Recieved: See full notification for more info`,
            GenericNotificationType.SUCCESS,
          );
        }

        const obj: {
          [serviceRequestId: string]: ServiceRequest;
        } = {};
        obj[request.id] = request;
        dispatch(
          updateEntities({
            serviceRequestsByFacilityId: (prev: ServiceRequestEntityState) => ({
              ...prev,
              ...{
                [request.facilityId]: { ...prev[request.facilityId], ...obj },
              },
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundBegan",
    (message: RoundBeganMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundBeganMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          //estimatedTimeRemaining: parsedMessage.estimatedTimeRemaining,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: SimulatorsEntitiesState) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundV2Began",
    (message: RoundV2BeganMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundV2BeganMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          //estimatedTimeRemaining: parsedMessage.estimatedTimeRemaining,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: SimulatorsEntitiesState) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundUpdate",
    (message: RoundUpdateMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundUpdateMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          estimatedTimeRemaining: parsedMessage.estimatedTimeRemaining,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: SimulatorsEntitiesState) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundV2Update",
    (message: RoundV2UpdateMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundV2UpdateMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          estimatedTimeRemaining: parsedMessage.estimatedTimeRemaining,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: SimulatorsEntitiesState) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundEnded",
    (message: RoundEndedMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundEndedMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          estimatedTimeRemaining: "",
          status: SimStatus.STATUS_SIM_EXIT,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: EntitiesById<SimulatorState>) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorRoundV2Ended",
    (message: RoundV2EndedMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<RoundV2EndedMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const round = parsedMessage.round;
        const roundObject = { [parsedMessage.roundId]: round };
        const simStateUpdate = {
          roundId: parsedMessage.roundId,
          estimatedTimeRemaining: "",
          status: SimStatus.STATUS_RND_END,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: EntitiesById<SimulatorState>) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
            rounds: (prev: RoundsEntitiesState) => ({
              ...prev,
              ...roundObject,
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorTimerUpdate",
    (message: TimerUpdateMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<TimerUpdateMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const simStateUpdate = {
          timerValue: timerStringToTimerArray(parsedMessage.value),
          timerStatus: parsedMessage.status,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: EntitiesById<SimulatorState>) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorStatus",
    (message: SimulatorStatusMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<SimulatorStatusMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const simStateUpdate = {
          status: parsedMessage.status,
          game: parsedMessage.game,
        };
        dispatch(
          updateEntities({
            simulatorStates: (prev: EntitiesById<SimulatorState>) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: {
                  ...prev[parsedMessage.simulatorId],
                  ...simStateUpdate,
                },
              },
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveTextScorecardMessage",
    (message: ScorecardMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<ScorecardMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const textScorecard = btoa(parsedMessage.textScorecard);
        dispatch(
          updateEntities({
            scorecardTextStates: (prev: ScorecardTextType) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: textScorecard,
              },
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveImageScorecardMessage",
    (message: ScorecardMessage | string) => (dispatch, getState) => {
      const parsedMessage = parseIfString<ScorecardMessage>(
        message,
        isMessageObject,
      );

      if (messageSimulatorBelongToUser(getState, parsedMessage.simulatorId)) {
        const imageScorecard = parsedMessage.base64ImageScorecard;
        dispatch(
          updateEntities({
            scorecardImageStates: (prev: ScorecardImageType) => ({
              ...prev,
              ...{
                [parsedMessage.simulatorId]: imageScorecard,
              },
            }),
          }),
        );
      }
    },
  )
  .add(
    "ReceiveSimulatorMessageProcessed",
    (message: ScorecardMessage | string) => () => {
      const parsedMessage = parseIfString<ScorecardMessage>(
        message,
        isMessageObject,
      );

      console.log(parsedMessage);
    },
  )
  .add(
    "receiveupdateddatamessage",
    (facilityId: string, updatedData: ClubhouseUpdateMessage) =>
      (dispatch, getState) => {
        const currentFacilityId = getCurrentFacilityId(getState); // useGetCurrentLocalFacilityId();
        console.log(
          "from receiveupdateddatamessage currentFacilityId: ",
          currentFacilityId,
          "facilityId: ",
          facilityId,
          updatedData,
        );
        if (currentFacilityId === facilityId) {
          const simulator = getSimulator(updatedData);

          if (simulator) {
            dispatch(
              updateEntities({
                simulators: (prev: SimulatorsEntitiesState) => ({
                  ...prev,
                  ...{
                    [simulator.id]: {
                      ...prev[simulator.id],
                      ...simulator,
                    },
                  },
                }),
              }),
            );
            return;
          }

          const simulators = getSimulators(updatedData);
          if (simulators) {
            // Handle simulators list update
            dispatch(
              updateEntities({
                simulators: (prev: SimulatorsEntitiesState) => {
                  const updatedSimulators = { ...prev };

                  // Assuming simulators is an array of SimulatorDto objects
                  simulators.forEach((simulator) => {
                    updatedSimulators[simulator.id] = {
                      ...(prev[simulator.id] || {}),
                      ...simulator,
                    };
                  });

                  return updatedSimulators;
                },
              }),
            );
            return;
          }
        }
      },
  )
  .add(
    "receivelatestsimulatorconnectionstatus",
    (
      facilityId: string,
      simulatorId: string,
      status: Clubhouse_SimulatorDevice,
    ) =>
      (dispatch, getState) => {
        const currentFacilityId = getCurrentFacilityId(getState); // useGetCurrentLocalFacilityId();
        console.log(
          "SignalR message: 'receivelatestsimulatorconnectionstatus' received:",
          { facilityId, simulatorId, status },
        );
        if (currentFacilityId !== facilityId) {
          return;
        }

        dispatch(
          updateEntities({
            simulators: (prev: SimulatorsEntitiesState) => {
              const existing = prev[simulatorId];
              if (!existing) return prev;
              console.log(
                "existing: ",
                existing,
                "simulatorId: ",
                simulatorId,
                "status: ",
                status,
              );
              return {
                ...prev,
                [simulatorId]: {
                  ...existing,
                  status: {
                    ...existing.status,
                    agConnect: status,
                  },
                },
              };
            },
          }),
        );
      },
  )
  .add(
    "receivelatestscreenshot",
    (
      facilityId: string,
      screenShotMetaData: SimulatorScreenShotsDto,
      screenShot: Uint8Array,
    ) =>
      (dispatch, getState) => {
        const currentFacilityId = getCurrentFacilityId(getState); //useGetCurrentLocalFacilityId();
        const screenShotMetaDataTyped = mapScreenshotDto(screenShotMetaData);
        console.log(
          "from receivelatestscreenshot currentFacilityId: ",
          currentFacilityId,
          "facilityId: ",
          facilityId,
        );

        if (currentFacilityId === facilityId) {
          dispatch(
            updateEntities({
              screenshots: (prev: ScreenshotsEntityType) => ({
                ...(prev || {}),
                ...{
                  [screenShotMetaDataTyped.simulatorId]: {
                    ...screenShotMetaDataTyped,
                    image: screenShot,
                  },
                },
              }),
            }),
          );
        }
      },
  );

export const signalRMiddleware = signalMiddleware({
  callbacks,
  connection,
  shouldConnectionStartImmediately: false,
});
